import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@veil/ui/components/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import { Skeleton } from "@veil/ui/components/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@veil/ui/components/table";
import { formatRelative } from "@veil/ui/lib/format";
import GitBranch from "lucide-react/dist/esm/icons/git-branch";
import History from "lucide-react/dist/esm/icons/history";
import RefreshCcw from "lucide-react/dist/esm/icons/refresh-ccw";
import { toast } from "sonner";
import { Link } from "wouter";
import { EmptyState, QueryError } from "@/components/query-state";
import { StatusBadge } from "@/components/status-badge";
import { api, unwrap } from "@/lib/api";
import { hasRole, useAuth } from "@/lib/auth-context";

interface ManifestResponse {
	success: boolean;
	manifest: unknown;
}

interface HistoryRow {
	id: string;
	status: string;
	startedAt: string;
	finishedAt: string | null;
	exitCode: number | null;
	username: string | null;
}

interface HistoryResponse {
	success: boolean;
	history: HistoryRow[];
}

export function Manifest() {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const manifest = useQuery({
		queryKey: ["manifest"],
		queryFn: () => unwrap(api.api.manifest.get()) as Promise<ManifestResponse>,
	});
	const history = useQuery({
		queryKey: ["manifest", "history"],
		queryFn: () =>
			unwrap(api.api.manifest.history.get()) as Promise<HistoryResponse>,
		refetchInterval: 10_000,
	});

	const regen = useMutation({
		mutationFn: () =>
			unwrap(api.api.manifest.regenerate.post()) as Promise<{
				operationId: string;
			}>,
		onSuccess: () => {
			toast.success("Regenerating manifest…");
			queryClient.invalidateQueries({ queryKey: ["manifest"] });
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Regenerate failed"),
	});

	return (
		<div className="space-y-6">
			<div className="flex items-end justify-between">
				<div>
					<h1 className="text-2xl font-semibold tracking-tight">Manifest</h1>
					<p className="text-sm text-muted-foreground">
						The published catalog every worker polls to discover available
						releases.
					</p>
				</div>
				{hasRole(user, "operator") && (
					<Button onClick={() => regen.mutate()} disabled={regen.isPending}>
						<RefreshCcw className="mr-2 h-4 w-4" /> Regenerate
					</Button>
				)}
			</div>

			<Card>
				<CardHeader>
					<CardTitle className="text-base">Current manifest</CardTitle>
				</CardHeader>
				<CardContent>
					{manifest.isLoading ? <Skeleton className="h-64" /> : null}
					{!manifest.isLoading && manifest.isError ? (
						<QueryError
							title="Couldn't read the manifest"
							error={manifest.error}
							onRetry={() => void manifest.refetch()}
							retrying={manifest.isFetching}
						/>
					) : null}
					{/* `manifest: null` is a real state — the webroot has no
					    manifest.json until something is published or regenerated. */}
					{!manifest.isLoading &&
					!manifest.isError &&
					!manifest.data?.manifest ? (
						<EmptyState
							icon={GitBranch}
							title="No manifest published yet"
							description="Publish a Chromium build or a profile bundle, or regenerate to write an empty catalog."
						/>
					) : null}
					{!manifest.isLoading &&
					!manifest.isError &&
					manifest.data?.manifest ? (
						<pre className="max-h-[600px] overflow-auto rounded-md bg-zinc-950 p-4 font-mono text-xs leading-relaxed text-zinc-100">
							{JSON.stringify(manifest.data.manifest, null, 2)}
						</pre>
					) : null}
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle className="text-base">Regenerate history</CardTitle>
				</CardHeader>
				<CardContent className="p-0">
					{history.isError ? (
						<div className="p-6">
							<QueryError
								title="Couldn't load regenerate history"
								error={history.error}
								onRetry={() => void history.refetch()}
								retrying={history.isFetching}
							/>
						</div>
					) : (history.data?.history ?? []).length === 0 &&
						!history.isLoading ? (
						<div className="p-6">
							<EmptyState
								icon={History}
								title="No regenerations recorded"
								description="Every manifest regeneration lands here with its operation log."
							/>
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Started</TableHead>
									<TableHead>Status</TableHead>
									<TableHead>Exit</TableHead>
									<TableHead>By</TableHead>
									<TableHead className="text-right">Detail</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{(history.data?.history ?? []).map((h) => (
									<TableRow key={h.id}>
										<TableCell>{formatRelative(h.startedAt)}</TableCell>
										<TableCell>
											<StatusBadge status={h.status} />
										</TableCell>
										<TableCell className="font-mono text-xs">
											{h.exitCode ?? "—"}
										</TableCell>
										<TableCell>{h.username ?? "—"}</TableCell>
										<TableCell className="text-right">
											<Link
												href={`/operations/${h.id}`}
												className="text-primary hover:underline"
											>
												open
											</Link>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
